import { useEffect, useRef, useState } from 'react';
import { anomalyReasonText } from '../utils/anomalySignals';
import apiService, { isApiAbortError } from '../services/apiService';
import { useUnitComps } from './useUnitComps';

export const AGENT_ERROR = 'Could not generate recommendations. Please try again.';
export const PRICE_HISTORY_ERROR = 'Could not load price history.';
// The Ask IronSight prefetch waits this long after a unit opens (P-UNIT-018).
export const AGENT_PREFETCH_MS = 500;

/**
 * The unit's query params for Ask IronSight (P-UNIT-019). laden_risk and
 * risk_tier go through the keys apiService reads for them (LadenhueterRisk,
 * RiskTier); before the redesign they were never set, so the agent never got
 * the unit's calibrated risk (decision D9, bug list).
 */
export const buildFastTrackMeta = (u) => ({
    make: u.MAKE,
    model: u.MODEL,
    category: u.CATEGORY,
    variant: u.VARIANT,
    new_used: u.NewUsedStatus === 'N' ? 'New' : 'Used',
    year: u.YEAR_MANUF,
    hours: u.ODOMETER,
    price: u.Best_Sell_Price,
    days_in_stock: u.DaysInStock,
    predicted_price: u.PredictedPrice > 0 ? u.PredictedPrice : null,
    days_to_sell: u.DaysToSellPrediction,
    days_to_sell_category: u.DaysToSellCategory,
    predicted_bucket: u.PredictedBucket,
    bucket_confidence: u.BucketConfidence,
    LadenhueterRisk: u.LadenhueterRisk,
    RiskTier: u.RiskTier,
    quote_count: u.QuoteCount,
    days_since_last_quote: u.DaysSinceLastQuote,
    anomaly_verdict: u.AnomalyVerdict,
    anomaly_reason: anomalyReasonText(u),
    gross_margin: u.GrossMargin,
    gross_margin_pct: u.GrossMarginPct,
    carrying_cost_daily: u.CarryingCostDaily,
    carrying_cost_to_date: u.CarryingCostToDate,
    margin_after_carrying_pct: u.MarginAfterCarryingPct,
    margin_at_sale_pct: u.MarginAtSalePct,
    mmy_median_asking_price: u.MMYMedianAskingPrice,
    mmy_median_days_in_stock: u.MMYMedianDaysInStock,
});

/**
 * Everything the unit detail loads for one unit, shared by today's pane
 * (UnitDetailPane) and the full unit page (UnitV2), so the two cannot drift:
 *
 * - eager on open, abortable (P-UNIT-017): listing (machinefinder), the
 *   12-month predictions, pipeline, quote-equipment and comps;
 * - lazy on the first visit of a tab, cached for the unit (P-UNIT-020):
 *   photos, enriched service history (refresh = force_refresh), price history
 *   (an error is kept apart from "no changes");
 * - the Ask IronSight prefetch (P-UNIT-018): debounced 500 ms after open,
 *   never aborted once started, a stale result dropped; regenerate skips the
 *   debounce and sends force_refresh. A failed call (the API answers null)
 *   now ends in the error state with "Try again" instead of "Analysing…"
 *   forever (D9);
 * - the margin calculator's price (P-UNIT-015), reset per unit.
 *
 * Every piece resets when the stock number changes (P-UNIT-016).
 *
 * Options (the full page only, U4 / N6 / N9): `agent: false` never starts
 * Ask IronSight (a committed unit: its advice is about selling the unit);
 * `eagerImages` loads the photos
 * on open (hero photo, tab count); `tractorHouse` loads the marketplace
 * detail on open when the unit has a listing (THStatus) and GA4 lazily on the
 * Overview tab when it has a website page (GAPagePath), both cached per unit.
 */
export default function useUnitDetail(unit, activeTab, { eagerImages = false, tractorHouse = false, agent = true } = {}) {
    const stock = unit.StockNumber;

    const [mfData, setMfData] = useState(null);
    const [mfLoading, setMfLoading] = useState(false);

    const [images, setImages] = useState([]);
    const [imagesLoading, setImagesLoading] = useState(false);
    const [imagesFetched, setImagesFetched] = useState(false);

    const [pipeline, setPipeline] = useState(null);
    const [pipelineLoading, setPipelineLoading] = useState(false);
    const [pipelineFetched, setPipelineFetched] = useState(false);

    const [quoteEquipment, setQuoteEquipment] = useState(null);
    const [quoteEquipmentLoading, setQuoteEquipmentLoading] = useState(false);
    const [quoteEquipmentFetched, setQuoteEquipmentFetched] = useState(false);

    const [serviceHistory, setServiceHistory] = useState(null);
    const [serviceHistoryLoading, setServiceHistoryLoading] = useState(false);
    const [serviceHistoryFetched, setServiceHistoryFetched] = useState(false);
    const [serviceHistoryForceRefresh, setServiceHistoryForceRefresh] = useState(false);

    const [priceHistory, setPriceHistory] = useState(null);
    const [priceHistoryLoading, setPriceHistoryLoading] = useState(false);
    const [priceHistoryError, setPriceHistoryError] = useState(null);
    const [priceHistoryFetched, setPriceHistoryFetched] = useState(false);

    const [fastTrackAgent, setFastTrackAgent] = useState(null);
    const [fastTrackLoading, setFastTrackLoading] = useState(false);
    const [fastTrackFetched, setFastTrackFetched] = useState(false);
    const [fastTrackError, setFastTrackError] = useState(null);
    const [fastTrackForceRefresh, setFastTrackForceRefresh] = useState(false);

    const [predictions12m, setPredictions12m] = useState(null);
    const [predictions12mLoading, setPredictions12mLoading] = useState(false);

    const [thDetail, setThDetail] = useState(null);
    const [thLoading, setThLoading] = useState(false);
    const [ga4Detail, setGa4Detail] = useState(null);
    const [ga4Loading, setGa4Loading] = useState(false);
    const [ga4Fetched, setGa4Fetched] = useState(false);

    const [calcPrice, setCalcPrice] = useState(null);

    // Which unit is open, so a background Ask IronSight prefetch that finishes
    // after the user has moved on doesn't write into the unit now displayed.
    // Assigned during render so it reflects the latest unit immediately.
    const openStockRef = useRef(stock);
    openStockRef.current = stock;
    // False once unmounted, so an in-flight prefetch (deliberately not aborted)
    // can't setState on an unmounted component when it resolves.
    const mountedRef = useRef(true);
    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    const { comps, compsLoading } = useUnitComps(unit);

    useEffect(() => {
        const controller = new AbortController();

        setImages([]);
        setImagesFetched(false);
        setPipeline(null);
        setPipelineFetched(false);
        setQuoteEquipment(null);
        setQuoteEquipmentFetched(false);
        setServiceHistory(null);
        setServiceHistoryFetched(false);
        setPriceHistory(null);
        setPriceHistoryError(null);
        setPriceHistoryFetched(false);
        setFastTrackAgent(null);
        setFastTrackFetched(false);
        setFastTrackError(null);
        setFastTrackForceRefresh(false);
        setCalcPrice(null);
        setThDetail(null);
        setGa4Detail(null);
        setGa4Fetched(false);

        setMfData(null);
        setMfLoading(true);
        setPredictions12m(null);
        setPredictions12mLoading(true);

        apiService.getUnitMachinefinderData(stock, { signal: controller.signal })
            .then((data) => setMfData(data))
            .catch((error) => {
                if (!isApiAbortError(error)) setMfData(null);
            })
            .finally(() => {
                if (!controller.signal.aborted) setMfLoading(false);
            });

        apiService.getPredictions12m(stock, { signal: controller.signal })
            .then((data) => { if (!controller.signal.aborted) setPredictions12m(data); })
            .catch((error) => { if (!isApiAbortError(error)) setPredictions12m(null); })
            .finally(() => { if (!controller.signal.aborted) setPredictions12mLoading(false); });

        return () => controller.abort();
    }, [stock]);

    const wantImages = eagerImages || activeTab === 'photos';
    useEffect(() => {
        if (!wantImages || imagesFetched) return undefined;

        const controller = new AbortController();
        setImagesLoading(true);

        apiService.getUnitImages(stock, { signal: controller.signal })
            .then((data) => {
                if (!controller.signal.aborted) {
                    setImages(data);
                    setImagesFetched(true);
                }
            })
            .catch((error) => {
                if (!isApiAbortError(error)) {
                    setImages([]);
                    setImagesFetched(true);
                }
            })
            .finally(() => {
                if (!controller.signal.aborted) setImagesLoading(false);
            });

        return () => controller.abort();
    }, [wantImages, imagesFetched, stock]);

    useEffect(() => {
        if (pipelineFetched) return undefined;

        const controller = new AbortController();
        setPipelineLoading(true);

        apiService.getUnitPipeline(stock, { signal: controller.signal })
            .then((data) => {
                if (!controller.signal.aborted) {
                    setPipeline(data);
                    setPipelineFetched(true);
                }
            })
            .catch((error) => {
                if (!isApiAbortError(error)) {
                    setPipeline(null);
                    setPipelineFetched(true);
                }
            })
            .finally(() => {
                if (!controller.signal.aborted) setPipelineLoading(false);
            });

        return () => controller.abort();
    }, [pipelineFetched, stock]);

    useEffect(() => {
        if (quoteEquipmentFetched) return undefined;

        const controller = new AbortController();
        setQuoteEquipmentLoading(true);

        apiService.getUnitQuoteEquipment(stock, { signal: controller.signal })
            .then((data) => {
                if (!controller.signal.aborted) {
                    setQuoteEquipment(data);
                    setQuoteEquipmentFetched(true);
                }
            })
            .catch((error) => {
                if (!isApiAbortError(error)) {
                    setQuoteEquipment(null);
                    setQuoteEquipmentFetched(true);
                }
            })
            .finally(() => {
                if (!controller.signal.aborted) setQuoteEquipmentLoading(false);
            });

        return () => controller.abort();
    }, [quoteEquipmentFetched, stock]);

    useEffect(() => {
        if (activeTab !== 'service' || serviceHistoryFetched) return undefined;

        const controller = new AbortController();
        setServiceHistoryLoading(true);
        const forceRefresh = serviceHistoryForceRefresh;
        if (forceRefresh) setServiceHistoryForceRefresh(false);

        apiService.getEnrichedServiceHistory(stock, { signal: controller.signal, forceRefresh })
            .then((data) => {
                if (!controller.signal.aborted) {
                    setServiceHistory(data);
                    setServiceHistoryFetched(true);
                }
            })
            .catch((error) => {
                if (!isApiAbortError(error)) {
                    setServiceHistory(null);
                    setServiceHistoryFetched(true);
                }
            })
            .finally(() => {
                if (!controller.signal.aborted) setServiceHistoryLoading(false);
            });

        return () => controller.abort();
    }, [activeTab, serviceHistoryFetched, stock]); // eslint-disable-line react-hooks/exhaustive-deps

    // Price history: lazily on the first Pricing & Usage visit. The getter
    // rethrows, so a failure is an error state distinct from "no changes".
    useEffect(() => {
        if (activeTab !== 'pricing' || priceHistoryFetched) return undefined;

        const controller = new AbortController();
        setPriceHistoryLoading(true);
        setPriceHistoryError(null);

        apiService.getUnitPriceHistory(stock, { signal: controller.signal })
            .then((data) => {
                if (!controller.signal.aborted) {
                    setPriceHistory(data);
                    setPriceHistoryFetched(true);
                }
            })
            .catch((error) => {
                if (!isApiAbortError(error)) {
                    setPriceHistory(null);
                    setPriceHistoryError(PRICE_HISTORY_ERROR);
                    setPriceHistoryFetched(true);
                }
            })
            .finally(() => {
                if (!controller.signal.aborted) setPriceHistoryLoading(false);
            });

        return () => controller.abort();
    }, [activeTab, priceHistoryFetched, stock]);

    // Marketplace detail (full page): on open, for a unit with a listing, so
    // the hero's "Open listing" and the Overview share one request.
    const thStatus = unit.THStatus;
    useEffect(() => {
        if (!tractorHouse || !thStatus) return undefined;
        let cancelled = false;
        setThLoading(true);
        apiService.getUnitTractorHouse(stock)
            .then((data) => { if (!cancelled) setThDetail(data); })
            .catch(() => { if (!cancelled) setThDetail(null); })
            .finally(() => { if (!cancelled) setThLoading(false); });
        return () => { cancelled = true; };
    }, [tractorHouse, thStatus, stock]);

    // Website engagement (full page): on the first Overview visit.
    const gaPath = unit.GAPagePath;
    useEffect(() => {
        if (!tractorHouse || !gaPath || activeTab !== 'overview' || ga4Fetched) return undefined;
        let cancelled = false;
        setGa4Loading(true);
        apiService.getUnitGA4(stock)
            .then((data) => { if (!cancelled) { setGa4Detail(data); setGa4Fetched(true); } })
            .catch(() => { if (!cancelled) { setGa4Detail(null); setGa4Fetched(true); } })
            .finally(() => { if (!cancelled) setGa4Loading(false); });
        return () => { cancelled = true; };
    }, [tractorHouse, gaPath, activeTab, ga4Fetched, stock]);

    // Ask IronSight prefetch (client ask 2026-09-09): start generating as soon
    // as the unit opens (any tab), so the analysis is ready by the time the
    // user reaches the tab. Debounced (arrowing through units fires nothing);
    // once started not aborted (it caches server-side), a stale completion is
    // dropped via openStockRef. A regenerate skips the debounce.
    useEffect(() => {
        if (!agent || fastTrackFetched) return undefined;
        const thisStock = stock;
        const forceRefresh = fastTrackForceRefresh;
        const delay = forceRefresh ? 0 : AGENT_PREFETCH_MS;
        const timer = setTimeout(() => {
            if (forceRefresh) setFastTrackForceRefresh(false);
            if (openStockRef.current === thisStock) setFastTrackLoading(true);
            setFastTrackError(null);
            apiService.getUnitFastTrackAgent(thisStock, buildFastTrackMeta(unit), { forceRefresh })
                .then((data) => {
                    if (!mountedRef.current || openStockRef.current !== thisStock) return;
                    // The API answers null on a failed call: that is an error,
                    // not "still analysing" (D9).
                    if (data == null) setFastTrackError(AGENT_ERROR);
                    setFastTrackAgent(data);
                    setFastTrackFetched(true);
                    setFastTrackLoading(false);
                })
                .catch((err) => {
                    if (!mountedRef.current || openStockRef.current !== thisStock || isApiAbortError(err)) return;
                    setFastTrackError(AGENT_ERROR);
                    setFastTrackFetched(true);
                    setFastTrackLoading(false);
                });
        }, delay);
        return () => clearTimeout(timer);
    }, [agent, fastTrackFetched, stock]); // eslint-disable-line react-hooks/exhaustive-deps

    const regenerateAgent = () => {
        setFastTrackForceRefresh(true);
        setFastTrackAgent(null);
        setFastTrackError(null);
        setFastTrackFetched(false);
    };

    const refreshServiceHistory = () => {
        setServiceHistoryForceRefresh(true);
        setServiceHistory(null);
        setServiceHistoryFetched(false);
    };

    // "generating" while the call runs, "ready" once an answer is in,
    // "error" after a failure, "idle" before the prefetch starts.
    let agentState = 'idle';
    if (fastTrackLoading) agentState = 'generating';
    else if (fastTrackError) agentState = 'error';
    else if (fastTrackAgent) agentState = 'ready';

    return {
        stock,
        mfData,
        mfLoading,
        images,
        imagesLoading,
        imagesFetched,
        pipeline,
        pipelineLoading,
        quoteEquipment,
        quoteEquipmentLoading,
        serviceHistory,
        serviceHistoryLoading,
        refreshServiceHistory,
        priceHistory,
        priceHistoryLoading,
        priceHistoryError,
        fastTrackAgent,
        fastTrackLoading,
        fastTrackError,
        agentState,
        regenerateAgent,
        predictions12m,
        predictions12mLoading,
        comps,
        compsLoading,
        calcPrice,
        setCalcPrice,
        thDetail,
        thLoading,
        ga4Detail,
        ga4Loading,
    };
}
